<script setup>
import PackagePrice from "@/components/settings/billing/partials/PackagePrice.vue";
import CardSpacer from "@/components/settings/ui/CardSpacer.vue";
import CardHeading from "@/components/settings/ui/CardHeading.vue";
import CardText from "@/components/settings/ui/CardText.vue";
import CardCheckmark from "@/components/settings/ui/CardCheckmark.vue";
import CardRow from "@/components/settings/ui/CardRow.vue";
import CardSection from "@/components/settings/ui/CardSection.vue";
import CardSubSection from "@/components/settings/ui/CardSubSection.vue";
import DefaultButton from "@/components/global-themed/buttons/DefaultButton.vue";
import {useBillingPackages} from "@/composables/useBillingPackages";
import {handle3dSecure} from "@/composables/useStripe";
import {errorToast} from "@/composables/useToasts";
import {computed, ref, watch} from "vue";
import {useMutation, useQueryClient} from "@tanstack/vue-query";
import axios from "axios";
import Swal from "sweetalert2";

const props = defineProps({
  product: {
    type: Object,
    required: true,
    default: () => {
    },
  },
  variant: {
    type: String,
    required: false,
    default: 'primary',
  }
});

const {isYearly, billingPeriod} = useBillingPackages();


const selectedPrice = computed(() => {
  return isYearly.value ? props.product.prices.yearly : props.product.prices.monthly;
});

const isBillingPeriodMismatch = computed(() => {
  return (isYearly.value ? 'YEAR' : 'MONTH') !== billingPeriod?.value;
});


const queryClient = useQueryClient();

const requires3dSecure = (error) => error?.response?.status === 402;

/**
 * The 3DS challenge runs after the mutation has already settled, so its pending state is tracked
 * separately to keep the button loading until the payment finally succeeds or fails.
 */
const is3dSecurePending = ref(false);

const confirmPackageChanged = async () => {
  await queryClient.invalidateQueries({queryKey: ['billing-packages']});
  await Swal.fire({
    title: `Plan changed successfully`,
    icon: 'success'
  });
};

const {mutate: changePackage, isPending, error: changePackageError} = useMutation({
  mutationFn: async ({priceId, preview = false}) => {
    const {data} = await axios.post('/ajax/v2/billing/packages/change', {
      priceId,
      preview,
    });
    return data;
  },
  onError: (error) => {
    if (requires3dSecure(error)) {
      // The prorated invoice needs 3DS. The watcher below runs the challenge.
      return;
    }

    if (error.response?.data?.message) {
      Swal.fire({
        title: 'Cannot change plan',
        html: error.response.data.message,
        icon: 'warning',
        confirmButtonText: 'OK',
      });
    } else {
      Swal.fire({
        title: 'Cannot change plan',
        html: 'Failed to change plan. Please try again.',
        icon: 'warning',
        confirmButtonText: 'OK',
      });
    }
  },
  onSuccess: async (data, variables) => {
    if (variables.preview) {
      const previewData = data.data;
      const message = previewData.is_upgrade
        ? (previewData.amount_due > 0
          ? `Changing your plan will charge ${previewData.formatted_amount_due} to your account.`
          : `You have enough credit to cover this change. You will not be charged.`)
        : `You will receive a credit of ${previewData.formatted_credit} on your next invoice.`;

      const result = await Swal.fire({
        title: 'Confirm plan change',
        html: message,
        icon: 'question',
        showCancelButton: true,
        reverseButtons: true,
        cancelButtonText: 'No, cancel',
        confirmButtonText: 'Yes, change plan',
      });

      if (result.isConfirmed) {
        changePackage({priceId: variables.priceId, preview: false});
      }

      return;
    }
    await confirmPackageChanged();
  },
});

/**
 * Stripe holds the plan change as a pending update until the prorated invoice is paid, so nothing has changed
 * yet at this point. A failed challenge therefore needs no cleanup, and a successful one re-runs the request so
 * the plan Hike shows catches up with the one Stripe has just applied.
 */
watch(changePackageError, async () => {
  if (!requires3dSecure(changePackageError.value)) {
    return;
  }

  is3dSecurePending.value = true;

  try {
    await handle3dSecure(changePackageError, async (result) => {
      if (result.error) {
        // Stripe is still holding the change back — drop it, so the customer isn't left with a payable
        // invoice that the Churnkey wall would then try to collect on.
        await axios.post('/ajax/v2/billing/packages/change/abandon');

        return;
      }

      changePackage({priceId: selectedPrice.value.id, preview: false});
    });
  } catch {
    // Stripe.js failing to run the challenge at all, rather than the customer failing it.
    errorToast('We were unable to verify your payment, please try again later.');
  } finally {
    is3dSecurePending.value = false;
  }
});

const isProcessing = computed(() => isPending.value || is3dSecurePending.value);
</script>

<template>
  <div class="tw-flex tw-w-80 tw-flex-col">
    <div
      v-if="product.current"
      class="tw-bg-primary tw-border-primary tw-rounded-t-2xl tw-py-3 tw-text-white tw-flex tw-justify-center tw-font-bold tw-text-base"
    >
      Current Plan
    </div>
    <div
      class="tw-w-80 tw-p-8 tw-pt-12 tw-border tw-flex tw-flex-col tw-text-neutral-900"
      :class="{
        'tw-border-primary  tw-bg-primary-100/25 tw-rounded-b-xl': product.current,
        'tw-border-neutral-300 tw-rounded-xl tw-mt-12' : !product.current
      }"
    >
      <div class="tw-flex tw-flex-col tw-gap-8 tw-items-center">
        <span class="tw-text-2xl tw-font-bold">
          {{ product.name }}
        </span>

        <PackagePrice
          :current="product.current"
          :price="selectedPrice"
          :isYearly="isYearly"
        />
      </div>

      <CardSpacer />

      <CardSection>
        <CardHeading>
          Deliverables
        </CardHeading>

        <CardSubSection>
          <CardRow>
            <CardCheckmark checked />
            <CardText>Website</CardText>
          </CardRow>

          <CardRow>
            <CardCheckmark checked />
            <CardText>Local</CardText>
          </CardRow>
        </CardSubSection>
      </CardSection>

      <CardSpacer />

      <CardSection>
        <CardHeading>
          Deliverables Quantity
        </CardHeading>

        <CardSubSection>
          <CardRow>
            <CardCheckmark checked />
            <CardText>
              {{ product.deliverables.number_of_pages }} x
              {{ product.deliverables.number_of_pages === 1 ? 'Page' : 'Pages' }}
            </CardText>
          </CardRow>
          <CardRow>
            <CardCheckmark checked />
            <CardText>
              {{ product.deliverables.number_of_locations }} x
              {{ product.deliverables.number_of_locations === 1 ? 'Location' : 'Locations' }}
            </CardText>
          </CardRow>
          <CardRow>
            <CardCheckmark checked />
            <CardText>
              {{ product.deliverables.number_of_blogs }} x
              {{ product.deliverables.number_of_blogs === 1 ? 'Blog Post' : 'Blog Posts' }}
            </CardText>
          </CardRow>
        </CardSubSection>
      </CardSection>

      <CardSpacer />

      <CardSection>
        <CardHeading>
          Agency Features
        </CardHeading>

        <CardSubSection>
          <CardRow>
            <CardCheckmark :checked="!!product.deliverables.has_whitelabel" />
            <CardText :disabled="!product.deliverables.has_whitelabel">
              White-label Features
            </CardText>
          </CardRow>
          <CardRow>
            <CardCheckmark :checked="!!product.deliverables.has_whitelabel" />
            <CardText :disabled="!product.deliverables.has_whitelabel">
              Lead-gen Audit Tool
            </CardText>
          </CardRow>
        </CardSubSection>
      </CardSection>

      <CardSpacer />

      <CardSection>
        <CardHeading>
          Agency Features
        </CardHeading>

        <CardSubSection>
          <CardRow>
            <CardCheckmark :checked="!!product.deliverables.has_whitelabel" />
            <CardText :disabled="!product.deliverables.has_whitelabel">
              White-label Features
            </CardText>
          </CardRow>
          <CardRow>
            <CardCheckmark :checked="!!product.deliverables.has_whitelabel" />
            <CardText :disabled="!product.deliverables.has_whitelabel">
              Lead-gen Audit Tool
            </CardText>
          </CardRow>
        </CardSubSection>
      </CardSection>

      <DefaultButton
        v-tooltip="{
          content: 'Please contact our support team to upgrade due to billing period mismatch.',
          placement: 'bottom',
          theme: 'dark-blue-tooltip',
          disabled: !isBillingPeriodMismatch
        }"
        :variant="variant"
        :disabled="product.current || isBillingPeriodMismatch || isProcessing"
        :loading="isProcessing"
        class="tw-mt-8"
        @click="changePackage({ priceId: selectedPrice.id, preview: true })"
      >
        Change Plan
      </DefaultButton>
    </div>
  </div>
</template>
